import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertOctagon, AlertTriangle, Info, ListPlus, ShieldCheck } from "lucide-react";
import { hasRole, useRole } from "@/context/RoleContext";

const SEV = {
  CRITICAL: { color: "#D32F2F", Icon: AlertOctagon, label: "Critical" },
  MAJOR: { color: "#F57C00", Icon: AlertTriangle, label: "Major" },
  MINOR: { color: "#FBC02D", Icon: Info, label: "Minor" },
};

export const RoPAConflictPanel = ({ assessmentId, recordId, compact = false, onChanged }) => {
  const role = useRole();
  const canCreateTask = hasRole(role, "control_owner");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/ropa/${assessmentId}/conflicts`, {
        params: recordId ? { record_id: recordId } : {},
      });
      setData(res);
    } catch (e) {
      toast.error("Could not load conflicts");
    } finally {
      setLoading(false);
    }
  }, [assessmentId, recordId]);

  useEffect(() => { load(); }, [load]);

  const createTask = async (findingId) => {
    setBusy(findingId);
    try {
      const { data: res } = await api.post(`/ropa/conflicts/${findingId}/task`);
      toast.success(res.task_created ? "Remediation task created" : "A task already exists for this conflict");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the task");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <Skeleton className={compact ? "h-28 rounded-xl" : "h-64 rounded-xl"} data-testid="ropa-conflicts-loading" />;

  const conflicts = data?.conflicts || [];

  return (
    <div
      className={compact ? "bg-white border border-slate-200 rounded-xl p-5" : "bg-white border border-slate-200 rounded-xl p-6"}
      data-testid={compact ? "ropa-conflict-panel-compact" : "ropa-conflict-panel"}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
          Conflicts {conflicts.length > 0 && `(${conflicts.length})`}
        </div>
        {conflicts.length > 0 && (
          <div className="flex gap-1.5" data-testid="ropa-conflict-severity-counts">
            {Object.entries(SEV).map(([key, cfg]) =>
              (data.by_severity?.[key] || 0) > 0 ? (
                <span
                  key={key}
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{ color: cfg.color, backgroundColor: `${cfg.color}1A` }}
                >
                  {data.by_severity[key]} {cfg.label}
                </span>
              ) : null
            )}
          </div>
        )}
      </div>

      {conflicts.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-[#388E3C]" data-testid="ropa-no-conflicts">
          <ShieldCheck size={15} /> No open DPDP conflicts detected.
        </div>
      ) : (
        <ul className="space-y-3">
          {conflicts.map((c) => {
            const cfg = SEV[c.severity] || SEV.MINOR;
            const Icon = cfg.Icon;
            return (
              <li
                key={c.finding_id}
                data-testid={`ropa-conflict-${c.ropa_sub_rule}-${c.entity_id}`}
                className="border border-slate-150 border-slate-200 rounded-lg p-3"
                style={{ borderLeft: `3px solid ${cfg.color}` }}
              >
                <div className="flex items-start gap-2">
                  <Icon size={15} style={{ color: cfg.color }} className="mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-[#003366] truncate">{c.entity_name}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                        {c.dpdp_ref}
                      </span>
                      <span className="text-[10px] font-mono text-slate-400">{c.ropa_code}</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{c.description}</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{c.suggested_action}</p>
                    {canCreateTask && (
                      <Button
                        size="sm" variant="outline"
                        className="mt-1 h-7 text-[11px]"
                        disabled={busy === c.finding_id || c.has_task}
                        data-testid={`ropa-conflict-task-${c.finding_id}`}
                        onClick={() => createTask(c.finding_id)}
                      >
                        <ListPlus size={12} className="mr-1.5" />
                        {c.has_task ? "Task exists" : "Create remediation task"}
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
